import { useState, useEffect, useCallback, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "wouter";
import { useGetMe, useGetMyProfile, useGetMyLinks, useUpdateProfile, getGetMeQueryKey, getGetMyProfileQueryKey, getGetMyLinksQueryKey } from "@workspace/api-client-react";
import { Navbar } from "@/components/layout/Navbar";
import { User, QrCode, BarChart3, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import MyProfile from "@/components/dashboard/MyProfile";
import QrCodeDisplay from "@/components/dashboard/QrCodeDisplay";
import Analytics from "@/components/dashboard/Analytics";
import { LivePreviewPanel, CustomisePanel, DEFAULT_CUSTOMIZATION } from "@/components/dashboard/ThemeCustomizer";
import type { PreviewProfile, PreviewLink } from "@/components/dashboard/ProfilePreview";
import type { UserCustomization } from "@commonqr/shared/config/themeEngine";

type Tab = "profile" | "qr" | "analytics";

const TABS: { id: Tab; label: string; icon: React.ElementType }[] = [
  { id: "profile", label: "My Profile", icon: User },
  { id: "qr", label: "QR Code", icon: QrCode },
  { id: "analytics", label: "Analytics", icon: BarChart3 },
];

export default function Dashboard() {
  const [, setLocation] = useLocation();
  const [activeTab, setActiveTab] = useState<Tab>("profile");
  const [liveProfile, setLiveProfile] = useState<PreviewProfile | null>(null);
  const [liveLinks, setLiveLinks] = useState<PreviewLink[] | null>(null);
  const [hasUnsaved, setHasUnsaved] = useState(false);
  const [previewViewMode, setPreviewViewMode] = useState<"mobile" | "desktop">("mobile");
  const [customization, setCustomization] = useState<UserCustomization>(DEFAULT_CUSTOMIZATION);
  const [customizationSeeded, setCustomizationSeeded] = useState(false);
  const syncThemeRef = useRef<((themeId: string | null, themeImageUrl: string | null) => void) | null>(null);
  const { toast } = useToast();

  const handleLiveChange = (p: any, l: any[]) => { setLiveProfile(p); setLiveLinks(l); setHasUnsaved(true); };

  const { data: user, isLoading: userLoading, error: userError } = useGetMe({ query: { queryKey: getGetMeQueryKey(), retry: false } });
  const { data: profile, isLoading: profileLoading } = useGetMyProfile({ query: { queryKey: getGetMyProfileQueryKey(), enabled: !!user } });
  const { data: links, isLoading: linksLoading } = useGetMyLinks({ query: { queryKey: getGetMyLinksQueryKey(), enabled: !!user } });
  const { mutateAsync: updateProfile } = useUpdateProfile();

  useEffect(() => {
    if (!profile || customizationSeeded) return;
    const saved: UserCustomization = {};
    if ((profile as any).themeUiStyle)     saved.uiStyle      = (profile as any).themeUiStyle;
    if ((profile as any).themeFontFamily)  saved.fontFamily   = (profile as any).themeFontFamily;
    if ((profile as any).themeFontSize)    saved.fontSize     = (profile as any).themeFontSize;
    if ((profile as any).themeTextColor)   saved.textColor    = (profile as any).themeTextColor;
    if ((profile as any).themeButtonColor) saved.buttonColor  = (profile as any).themeButtonColor;
    if ((profile as any).themeButtonStyle) saved.buttonStyle  = (profile as any).themeButtonStyle;
    if ((profile as any).themeLinkLayout)  saved.linkLayout   = (profile as any).themeLinkLayout;
    if ((profile as any).themeIconColor)   saved.iconColor    = (profile as any).themeIconColor;
    if (Object.keys(saved).length > 0) setCustomization(saved);
    setCustomizationSeeded(true);
    setLiveProfile({ name: profile.name, bio: profile.bio || undefined, location: profile.location || undefined, avatarUrl: profile.avatarUrl || undefined, username: profile.username, isVerified: profile.isVerified, scanCount: profile.scanCount ?? 0, coverFrom: (profile as any).coverFrom || undefined, coverTo: (profile as any).coverTo || undefined, themeId: profile.themeId ?? null, themeImageUrl: profile.themeImageUrl ?? null, linkLayout: (profile as any).themeLinkLayout ?? undefined } as any);
  }, [profile, customizationSeeded]);

  const handleTabChange = (tab: Tab) => {
    if (hasUnsaved && activeTab === "profile") {
      const ok = window.confirm("You have unsaved changes. Switch tabs anyway?");
      if (!ok) return;
    }
    setActiveTab(tab);
  };

  useEffect(() => { if (userError) setLocation("/login"); }, [userError, setLocation]);

  const handleApply = useCallback(async () => {
    try {
      await updateProfile({ data: { themeUiStyle: customization.uiStyle ?? null, themeFontFamily: customization.fontFamily ?? null, themeFontSize: customization.fontSize ?? null, themeTextColor: customization.textColor ?? null, themeButtonColor: customization.buttonColor ?? null, themeButtonStyle: customization.buttonStyle ?? null, themeLinkLayout: customization.linkLayout ?? null, themeIconColor: customization.iconColor ?? null, themeId: liveProfile?.themeId ?? (profile as any)?.themeId ?? null, themeImageUrl: liveProfile?.themeImageUrl ?? (profile as any)?.themeImageUrl ?? null } as any });
      toast({ title: "Customisation saved", description: "Your theme settings have been applied." });
    } catch (err) { console.error("[ThemeCustomizer] Failed to persist customization:", err); toast({ title: "Failed to save", description: "Could not apply customisation.", variant: "destructive" }); }
  }, [customization, updateProfile, liveProfile, profile]);

  const handleCustomizationChange = useCallback((patch: Partial<UserCustomization>) => { setCustomization(prev => ({ ...prev, ...patch })); if (patch.linkLayout !== undefined) setLiveProfile((prev: any) => prev ? { ...prev, linkLayout: patch.linkLayout } : prev); }, []);
  const handleCustomizationReset = useCallback(() => setCustomization(DEFAULT_CUSTOMIZATION), []);

  // handleThemeChange: no reference to previewProfile — avoids TDZ crash.
  // liveProfile is seeded from profile data via useEffect below, so prev is
  // always populated by the time a user can click a theme card.
  const handleThemeChange = useCallback((themeId: string | null, themeImageUrl: string | null) => {
    setLiveProfile((prev: any) => prev ? { ...prev, themeId, themeImageUrl } : { themeId, themeImageUrl });
    syncThemeRef.current?.(themeId, themeImageUrl);
  }, []);

  if (userLoading || profileLoading || linksLoading) return (
    <div className="min-h-screen flex items-center justify-center bg-muted/20">
      <div className="flex flex-col items-center gap-3">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">Loading your dashboard...</p>
      </div>
    </div>
  );

  if (!user || !profile) return null;

  const previewLinks: PreviewLink[] = (links || []).slice().sort((a, b) => (a.sortOrder ?? 99) - (b.sortOrder ?? 99)).map((l) => ({ id: l.id, platform: l.platform, label: l.label || l.platform, url: l.url, sortOrder: l.sortOrder }));

  const previewProfile: PreviewProfile = { name: profile.name, bio: profile.bio || undefined, location: profile.location || undefined, avatarUrl: profile.avatarUrl || undefined, username: profile.username, isVerified: profile.isVerified, scanCount: profile.scanCount ?? 0, coverFrom: (profile as any).coverFrom || undefined, coverTo: (profile as any).coverTo || undefined, themeId: profile.themeId ?? null, themeImageUrl: profile.themeImageUrl ?? null, linkLayout: (profile as any).themeLinkLayout ?? undefined };


  const activeProfile = liveProfile ?? previewProfile;
  const activeLinks = liveLinks ?? previewLinks;

  return (
    <div className="min-h-screen bg-muted/20">
      <Helmet>
        <title>Dashboard — CommonQR</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <Navbar />
      <div className="border-b border-border bg-background/80 backdrop-blur-sm sticky top-[57px] z-40">
        <div className="max-w-screen-2xl mx-auto px-2 sm:px-3 lg:px-4">
          <div className="flex items-center justify-between h-12 px-2">
            <div className="flex items-center gap-1 h-full">
              {TABS.map((tab) => { const Icon = tab.icon; return (<button key={tab.id} onClick={() => handleTabChange(tab.id)} className={`flex items-center gap-2 px-4 h-full text-xs font-medium border-b-2 transition-colors ${activeTab === tab.id ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}><Icon className="w-4 h-4" />{tab.label}</button>); })}
            </div>
            <div className="flex items-center gap-3 shrink-0 pr-2">
              <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center text-primary font-bold text-xs">{(profile.name ?? "U").charAt(0).toUpperCase()}</div>
              <div className="flex flex-col leading-tight">
                <span className="text-xs font-semibold text-foreground">{profile.name}</span>
                <span className="text-[11px] text-muted-foreground">@{profile.username}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-screen-2xl mx-auto px-2 sm:px-3 lg:px-4 py-6">
        {activeTab === "profile" && (
          <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,42fr)_minmax(0,33fr)_minmax(0,25fr)] gap-6 items-start">
            <div className="bg-card rounded-3xl border border-border/50 shadow-xl shadow-black/5 p-6 md:p-8 min-h-[600px]">
              <MyProfile profile={profile} links={links || []} onLiveChange={handleLiveChange} syncThemeRef={syncThemeRef} />
            </div>
            <div className="hidden xl:block sticky top-[105px]">
              <LivePreviewPanel profile={activeProfile} links={activeLinks} viewMode={previewViewMode} onViewModeChange={setPreviewViewMode} customization={customization} username={profile.username} />
            </div>
            <div className="hidden xl:block sticky top-[105px]">
              <CustomisePanel profile={activeProfile} customization={customization} onChange={handleCustomizationChange} onApply={handleApply} onReset={handleCustomizationReset} onThemeChange={handleThemeChange} />
            </div>
          </div>
        )}
        {activeTab !== "profile" && (
          <div className="bg-card rounded-3xl border border-border/50 shadow-xl shadow-black/5 p-6 md:p-8 min-h-[600px]">
            {activeTab === "qr" && (<QrCodeDisplay username={profile.username} customQrColour={profile.customQrColour} qrBgColor={profile.qrBgColor} qrFrame={profile.qrFrame} avatarUrl={profile.avatarUrl} />)}
            {activeTab === "analytics" && (<Analytics currentPlan={(profile as any).currentPlan ?? "free"} profileId={profile.id} />)}
          </div>
        )}
      </div>
    </div>
  );
}
